/**
 * Flow C's calls. Every one goes through `../../api/client.js` and nothing else.
 *
 * ── WHAT MOVED OUT OF `data.ts` AND WHAT DELIBERATELY DID NOT ────────────────
 * Everything that varies per conversation now comes from the Worker: the list on C1, the
 * thread and the card on C2, and the whole of C3, C4 and C5. Those were constants, so
 * every conversation rendered রহিম ইলেকট্রিক at ৪.৭ ★ — the same defect
 * `routes/search/client.ts § SearchResultItem` describes for the search door, where a
 * plumber search returned an air-conditioning technician with a made-up rating.
 *
 * What stayed in `data.ts` is the CHROME: C1's title, its lede, its two group headings,
 * the three example sentences and the two composer placeholders. Those belong to the
 * frame rather than to any conversation — they are drawn identically whatever the list
 * contains, and a server round trip to be told what a screen's own heading is would be a
 * round trip that can fail and leave a heading blank.
 *
 * Every `_bn` field that DOES arrive here is already localised (docs/05 § conventions).
 * The client composes no Bangla and joins no two strings into a third.
 */
import type {
  ConversationBudget,
  ConversationCompare,
  ConversationDetail,
  ConversationListResponse,
  ConversationPlan,
  CreateConversationRequest,
} from '@oprappo/contracts';
import { api } from '../../api/client.js';

/** `/v1` belongs to the path, not to a base — see the transport. */
export const CONVERSATIONS_PATH = '/v1/ai/conversations';

/** C1 · আপনার অনুরোধগুলো. */
export function fetchConversations(signal?: AbortSignal): Promise<ConversationListResponse> {
  return api.get<ConversationListResponse>(CONVERSATIONS_PATH, { signal });
}

/** C2 · কথোপকথন. */
export function fetchConversation(id: string, signal?: AbortSignal): Promise<ConversationDetail> {
  return api.get<ConversationDetail>(`${CONVERSATIONS_PATH}/${encodeURIComponent(id)}`, { signal });
}

/**
 * Open a thread from C1's composer.
 *
 * The search hand-off does NOT go through here: `POST /v1/search` opens the thread
 * server-side and returns `conversation_id`, and the results screen navigates to it. Two
 * clients opening threads for the same sentence would be two threads.
 */
export function openConversation(body: CreateConversationRequest): Promise<ConversationDetail> {
  return api.post<ConversationDetail>(CONVERSATIONS_PATH, body);
}

/**
 * Say one more thing in a thread. C2's composer and its objection chips.
 *
 * ANSWERS WITH THE WHOLE THREAD, not with an ack — the rule flow F already follows: a
 * screen that patches its own local copy after a write is a screen that will eventually
 * disagree with the server about what was said.
 *
 * No `idempotencyKey`. The transport offers one and this route must not take it: a
 * retried turn is a duplicate message in a conversation, which is visible and harmless,
 * and an idempotency key here would silently swallow a customer deliberately sending the
 * same short sentence twice — which in a chat is a thing people do.
 */
export function addTurn(id: string, message_bn: string): Promise<ConversationDetail> {
  return api.post<ConversationDetail>(`${CONVERSATIONS_PATH}/${encodeURIComponent(id)}/turns`, {
    message_bn,
  });
}

/** C3 · তুলনা. 404 when this thread has nothing to compare — see `conversations/copy.ts`. */
export function fetchCompare(id: string, signal?: AbortSignal): Promise<ConversationCompare> {
  return api.get<ConversationCompare>(
    `${CONVERSATIONS_PATH}/${encodeURIComponent(id)}/compare`,
    { signal },
  );
}

/** C4 · পরিকল্পনা. */
export function fetchPlan(id: string, signal?: AbortSignal): Promise<ConversationPlan> {
  return api.get<ConversationPlan>(`${CONVERSATIONS_PATH}/${encodeURIComponent(id)}/plan`, {
    signal,
  });
}

/** C5 · বাজেট অনুমান. */
export function fetchBudget(id: string, signal?: AbortSignal): Promise<ConversationBudget> {
  return api.get<ConversationBudget>(`${CONVERSATIONS_PATH}/${encodeURIComponent(id)}/budget`, {
    signal,
  });
}
